import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import { MonthlySummaryRow } from "../types/monthlySummaryTypes";

interface MonthlySummaryTableProps {
  rows: MonthlySummaryRow[];
}

// Formatea un número a 2 decimales; vacío/indefinido -> "0"
const fmt = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(Number(value))) return "0";
  const num = Number(value);
  return Number.isInteger(num) ? num.toFixed(0) : num.toFixed(2);
};

// Placeholder de las columnas cuya fuente todavía no existe (programa del DP / plan de obra).
const PENDING = "—";

// Rendimientos y valores opcionales: la ausencia de dato se muestra como "—", no como 0,
// porque un rendimiento 0 y un rendimiento sin calcular significan cosas distintas.
const fmtOptional = (value: number | null | undefined): string =>
  value === null || value === undefined || isNaN(Number(value)) ? PENDING : fmt(value);

// Mismos estilos compactos que el Resumen Semanal, para que los dos informes se lean igual.
const headStyle = {
  position: "sticky" as const,
  top: 0,
  zIndex: 1,
  backgroundColor: "white",
  color: "#004F86",
  fontWeight: "bold",
  fontSize: "0.6rem",
  padding: "1px 2px",
  lineHeight: 1.1,
  textAlign: "center" as const,
  verticalAlign: "bottom" as const,
  whiteSpace: "normal" as const,
  wordBreak: "break-word" as const,
  borderRight: "1px solid #e0e0e0",
};
const bodyStyle = {
  fontSize: "0.6rem",
  padding: "1px 2px",
  textAlign: "center" as const,
  verticalAlign: "middle" as const,
  whiteSpace: "normal" as const,
  wordBreak: "break-word" as const,
  borderRight: "1px solid #f0f0f0",
};
const headActivity = { ...headStyle, minWidth: 90, textAlign: "left" as const };
const bodyActivity = { ...bodyStyle, textAlign: "left" as const };
// Columnas sin fuente de datos, atenuadas para que se distingan de un valor real.
const bodyPending = { ...bodyStyle, color: "#b0b0b0", fontStyle: "italic" as const };
// Columnas de rendimiento, con un fondo suave para agruparlas visualmente.
const bodyYield = { ...bodyStyle, backgroundColor: "#f5f9fd" };

const MonthlySummaryTable: React.FC<MonthlySummaryTableProps> = ({ rows }) => {
  return (
    <Grid container sx={{ height: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          maxHeight: { xs: "55vh", md: "calc(100vh - 260px)" },
          overflow: "auto",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ width: "100%", minWidth: 1500, borderCollapse: "collapse" }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={headStyle}>Id</TableCell>
              <TableCell sx={headStyle}>Etapa</TableCell>
              <TableCell sx={headActivity}>Actividad</TableCell>
              <TableCell sx={headStyle}>Subcontrato</TableCell>
              <TableCell sx={headStyle}>Fecha Fin. Inicial</TableCell>
              <TableCell sx={headStyle}>Unidad</TableCell>
              <TableCell sx={headStyle}>Cantidad</TableCell>
              <TableCell sx={headStyle}>Acum. anterior</TableCell>
              <TableCell sx={headStyle}>Acum. Prog. Inicial</TableCell>
              <TableCell sx={headStyle}>Rend. Oferta</TableCell>
              <TableCell sx={headStyle}>Desvío Prog. Inicial</TableCell>
              <TableCell sx={headStyle}>Rend. Acumulado</TableCell>
              <TableCell sx={headStyle}>Ejec. periodo</TableCell>
              <TableCell sx={headStyle}>Rend. periodo</TableCell>
              <TableCell sx={headStyle}>Acum. Actual</TableCell>
              <TableCell sx={headStyle}>Avance a la fecha</TableCell>
              <TableCell sx={headStyle}>Prog. periodo</TableCell>
              <TableCell sx={headStyle}>Desv. periodo</TableCell>
              <TableCell sx={headStyle}>Núm. Cuadrillas</TableCell>
              <TableCell sx={headStyle}>Plan M+1</TableCell>
              <TableCell sx={headStyle}>Rend. Pendiente</TableCell>
              <TableCell sx={headStyle}>Nva. Fecha Fin.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow
                key={`${row.activityId}-${idx}`}
                hover
                sx={{ "&:hover td": { backgroundColor: "#e3f2fd !important" } }}
              >
                <TableCell sx={bodyStyle}>{idx + 1}</TableCell>
                <TableCell sx={bodyStyle}>{row.section ?? ""}</TableCell>
                <TableCell sx={bodyActivity}>
                  {row.name ?? ""} {row.subActivity ? `(${row.subActivity})` : ""}
                </TableCell>
                {/* C - Subcontrato */}
                <TableCell sx={bodyPending}>{row.subcontract ?? PENDING}</TableCell>
                {/* D - Fecha Finalización Inicial */}
                <TableCell sx={bodyPending}>{row.initialFinishDate ?? PENDING}</TableCell>
                <TableCell sx={bodyStyle}>{row.unit}</TableCell>
                {/* F - Cantidad */}
                <TableCell sx={bodyStyle}>{fmt(row.totalScope)}</TableCell>
                {/* G - Acumulado anterior */}
                <TableCell sx={bodyStyle}>{fmt(row.previousExecuted)}</TableCell>
                {/* H - Acumulado Programa Inicial */}
                <TableCell sx={bodyPending}>{fmtOptional(row.initialProgramAccum)}</TableCell>
                {/* I - Rendimiento Oferta */}
                <TableCell sx={bodyYield}>{fmtOptional(row.offerYield)}</TableCell>
                {/* K - Desvío Programa Inicial */}
                <TableCell sx={bodyPending}>{fmtOptional(row.initialProgramDeviation)}</TableCell>
                {/* L - Rendimiento Acumulado */}
                <TableCell sx={bodyYield}>{fmtOptional(row.accumulatedYield)}</TableCell>
                {/* M - Ejecutadas en el periodo */}
                <TableCell sx={bodyStyle}>{fmt(row.currentExecuted)}</TableCell>
                {/* N - Rendimiento en el Periodo */}
                <TableCell sx={bodyYield}>{fmtOptional(row.periodYield)}</TableCell>
                {/* O - Acumulado Actual */}
                <TableCell sx={bodyStyle}>{fmt(row.currentAccumulation)}</TableCell>
                {/* P - Avance a la fecha */}
                <TableCell sx={bodyStyle}>{Math.floor(row.activityProgress ?? 0)}%</TableCell>
                {/* Q - Programado en el periodo */}
                <TableCell sx={bodyStyle}>{fmt(row.programmedAdvance)}</TableCell>
                {/* R - Desviación en el periodo */}
                <TableCell sx={bodyStyle}>{fmt(row.periodDeviation)}</TableCell>
                {/* S - Núm. Cuadrillas */}
                <TableCell sx={bodyStyle}>{fmt(row.executedCrews)}</TableCell>
                {/* T - Plan M+1 */}
                <TableCell sx={bodyPending}>{fmtOptional(row.planNextMonth)}</TableCell>
                {/* U - Rendimiento Pendiente/Estimado */}
                <TableCell sx={bodyPending}>{fmtOptional(row.pendingYield)}</TableCell>
                {/* V - Nva. Fecha Finalización */}
                <TableCell sx={bodyPending}>{row.newFinishDate ?? PENDING}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default MonthlySummaryTable;
